import { useRef, useState, useCallback, useEffect } from "react";

/**
 * useSound — Web Audio API hook
 * Provides procedural synthwave background music + sound effects.
 * No external files, no CDN, no licensing issues.
 */
export function useSound() {
  const ctxRef = useRef(null);
  const masterGainRef = useRef(null);
  const musicGainRef = useRef(null);
  const sfxGainRef = useRef(null);
  const musicNodesRef = useRef([]); // oscillators / intervals for current track
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [musicEnabled, setMusicEnabled] = useState(true);
  const soundEnabledRef = useRef(true);
  const musicEnabledRef = useRef(true);

  // ── Init AudioContext on first interaction ─────────────────────────────────
  const getCtx = useCallback(() => {
    if (!ctxRef.current) {
      ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      masterGainRef.current = ctxRef.current.createGain();
      masterGainRef.current.gain.value = 0.7;
      masterGainRef.current.connect(ctxRef.current.destination);

      musicGainRef.current = ctxRef.current.createGain();
      musicGainRef.current.gain.value = 0.18;
      musicGainRef.current.connect(masterGainRef.current);

      sfxGainRef.current = ctxRef.current.createGain();
      sfxGainRef.current.gain.value = 0.6;
      sfxGainRef.current.connect(masterGainRef.current);
    }
    if (ctxRef.current.state === "suspended") {
      ctxRef.current.resume();
    }
    return ctxRef.current;
  }, []);

  // ── Low-level helpers ──────────────────────────────────────────────────────

  const isFiniteNum = (v) => typeof v === "number" && isFinite(v) && !isNaN(v);

  const playOsc = useCallback((freq, type, startTime, duration, gainVal, destination, detune = 0) => {
    const ctx = ctxRef.current;
    if (!ctx || !destination) return;
    if (!isFiniteNum(freq) || !isFiniteNum(startTime) || !isFiniteNum(duration) || !isFiniteNum(gainVal) || duration <= 0) return;
    try {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = type;
      osc.frequency.value = Math.max(1, freq);
      osc.detune.value = isFiniteNum(detune) ? detune : 0;
      const t0 = Math.max(ctx.currentTime, startTime);
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(Math.max(0.001, gainVal), t0 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
      osc.connect(g);
      g.connect(destination);
      osc.start(t0);
      osc.stop(t0 + duration + 0.05);
    } catch (e) { /* ignore audio errors */ }
  }, []);

  const playNoise = useCallback((startTime, duration, gainVal, destination, filterFreq = 2000) => {
    const ctx = ctxRef.current;
    if (!ctx || !destination) return;
    if (!isFiniteNum(startTime) || !isFiniteNum(duration) || !isFiniteNum(gainVal) || duration <= 0) return;
    try {
      const safeDuration = Math.max(0.01, Math.min(duration, 2));
      const bufSize = Math.floor(ctx.sampleRate * safeDuration);
      if (bufSize <= 0) return;
      const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const filter = ctx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.value = isFiniteNum(filterFreq) ? Math.max(10, filterFreq) : 2000;
      filter.Q.value = 0.5;
      const g = ctx.createGain();
      const t0 = Math.max(ctx.currentTime, startTime);
      g.gain.setValueAtTime(Math.max(0.001, gainVal), t0);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + safeDuration);
      src.connect(filter);
      filter.connect(g);
      g.connect(destination);
      src.start(t0);
      src.stop(t0 + safeDuration);
    } catch (e) { /* ignore audio errors */ }
  }, []);

  // ── Stop background music ──────────────────────────────────────────────────

  const stopMusic = useCallback(() => {
    musicNodesRef.current.forEach(n => {
      try {
        if (typeof n === "number") clearInterval(n);
        else { n.stop?.(); n.disconnect?.(); }
      } catch (_) {}
    });
    musicNodesRef.current = [];
  }, []);

  // ── Synthwave background music ─────────────────────────────────────────────
  // Chord progression: Am - F - C - G (classic synthwave)
  // Bass + pads + arpeggio

  const startMusic = useCallback(() => {
    const ctx = getCtx();
    if (!ctx || !musicEnabledRef.current) return;
    stopMusic(); // всегда чистим перед запуском

    const dest = musicGainRef.current;
    if (!dest) return;
    const isFiniteNum = (v) => typeof v === "number" && isFinite(v) && !isNaN(v);
    const bpm = 118;
    const beat = 60 / bpm;
    const bar = beat * 4;

    // Chord root frequencies (A3, F3, C4, G3)
    const chordRoots = [220, 174.61, 261.63, 196];
    // Pad notes for each chord (root + fifth + octave)
    const chordPads = [
      [220, 329.63, 440],   // Am
      [174.61, 261.63, 349.23], // F
      [261.63, 392, 523.25],    // C
      [196, 293.66, 392],       // G
    ];
    // Arpeggio notes per chord
    const chordArps = [
      [220, 261.63, 329.63, 440],
      [174.61, 220, 261.63, 349.23],
      [261.63, 329.63, 392, 523.25],
      [196, 246.94, 293.66, 392],
    ];

    let chordIdx = 0;
    let tick = 0;

    const scheduleBar = (when) => {
      if (!isFiniteNum(when) || when < 0) return;
      const chord = chordIdx % 4;
      const roots = chordRoots[chord];
      const pads = chordPads[chord];
      const arps = chordArps[chord];

      // Bass — root note, two hits per bar
      playOsc(roots / 2, "sawtooth", when, beat * 1.8, 0.9, dest);
      playOsc(roots / 2, "sawtooth", when + beat * 2, beat * 1.8, 0.9, dest);

      // Pad — soft chord stab
      pads.forEach(f => {
        playOsc(f, "sawtooth", when, bar * 0.9, 0.3, dest, 5);
        playOsc(f, "sawtooth", when, bar * 0.9, 0.3, dest, -5);
      });

      // Arpeggio — 16th notes
      for (let i = 0; i < 16; i++) {
        const arpFreq = arps[i % arps.length];
        const arpTime = when + i * (beat / 4);
        playOsc(arpFreq, "square", arpTime, beat / 4 * 0.7, 0.25, dest);
      }

      // Hi-hat noise
      for (let i = 0; i < 8; i++) {
        playNoise(when + i * (beat / 2), 0.05, 0.4, dest, 8000);
      }

      // Kick — low thud on beats 1 and 3
      [0, beat * 2].forEach(offset => {
        if (!ctx || !dest) return;
        try {
          const kickG = ctx.createGain();
          const t0 = when + offset;
          kickG.gain.setValueAtTime(1.0, t0);
          kickG.gain.exponentialRampToValueAtTime(0.001, t0 + 0.25);
          kickG.connect(dest);
          const kickOsc = ctx.createOscillator();
          kickOsc.frequency.setValueAtTime(150, t0);
          kickOsc.frequency.exponentialRampToValueAtTime(40, t0 + 0.2);
          kickOsc.connect(kickG);
          kickOsc.start(t0);
          kickOsc.stop(t0 + 0.3);
        } catch(e) {}
      });

      // Snare — beat 2 and 4
      [beat, beat * 3].forEach(offset => {
        playNoise(when + offset, 0.12, 0.6, dest, 1500);
        playOsc(200, "sine", when + offset, 0.1, 0.3, dest);
      });

      chordIdx++;
    };

    // Schedule 2 bars ahead, re-schedule every bar
    let nextBarTime = ctx.currentTime + 0.1;
    scheduleBar(nextBarTime);
    scheduleBar(nextBarTime + bar);
    nextBarTime += bar * 2;

    const intervalId = setInterval(() => {
      if (!musicEnabledRef.current || !ctxRef.current) {
        clearInterval(intervalId);
        return;
      }
      if (ctxRef.current.state === "suspended") ctxRef.current.resume();
      // Keep 2 bars scheduled ahead
      while (nextBarTime < ctxRef.current.currentTime + bar * 2) {
        scheduleBar(nextBarTime);
        nextBarTime += bar;
      }
    }, (bar * 1000) / 2);

    musicNodesRef.current.push(intervalId);
  }, [getCtx, stopMusic, playOsc, playNoise]);

  // ── Sound effects ──────────────────────────────────────────────────────────

  const playSound = useCallback((type) => {
    if (!soundEnabledRef.current) return;
    const ctx = getCtx();
    if (!ctx) return;
    const dest = sfxGainRef.current;
    if (!dest) return;
    const now = ctx.currentTime;
    if (!isFiniteNum(now)) return;

    try {
      switch (type) {

      case "click": {
        // Soft UI click
        playOsc(880, "sine", now, 0.06, 0.3, dest);
        break;
      }

      case "player_join": {
        // Ascending two-note chime
        playOsc(523.25, "sine", now, 0.15, 0.5, dest);
        playOsc(783.99, "sine", now + 0.12, 0.2, dest, 0.6, dest);
        break;
      }

      case "game_start": {
        // Rising fanfare arpeggio
        const notes = [261.63, 329.63, 392, 523.25, 659.25];
        notes.forEach((f, i) => playOsc(f, "sawtooth", now + i * 0.1, 0.3, 0.5, dest));
        break;
      }

      case "correct": {
        // Happy ascending chord
        [523.25, 659.25, 783.99].forEach((f, i) =>
          playOsc(f, "sine", now + i * 0.07, 0.35, 0.55, dest)
        );
        break;
      }

      case "wrong": {
        // Low descending buzzer
        playOsc(220, "sawtooth", now, 0.12, 0.6, dest);
        playOsc(180, "sawtooth", now + 0.1, 0.15, 0.5, dest);
        break;
      }

      case "vote": {
        // Soft "pop"
        playOsc(440, "sine", now, 0.08, 0.4, dest);
        playOsc(554.37, "sine", now + 0.05, 0.1, 0.3, dest);
        break;
      }

      case "tick": {
        // Metronome tick (crocodile timer)
        playOsc(1200, "square", now, 0.04, 0.25, dest);
        break;
      }

      case "tick_urgent": {
        // Urgent tick for last 10 seconds
        playOsc(1600, "square", now, 0.05, 0.4, dest);
        playNoise(now, 0.04, 0.15, dest, 4000);
        break;
      }

      case "time_up": {
        // Descending alarm
        [800, 600, 400].forEach((f, i) =>
          playOsc(f, "sawtooth", now + i * 0.15, 0.2, 0.6, dest)
        );
        break;
      }

      case "round_end": {
        // Short victory stab
        [392, 523.25, 659.25].forEach((f, i) =>
          playOsc(f, "sine", now + i * 0.08, 0.25, 0.5, dest)
        );
        break;
      }

      case "game_end": {
        // Full victory fanfare
        const fanfare = [261.63, 329.63, 392, 523.25, 659.25, 783.99, 1046.5];
        fanfare.forEach((f, i) => {
          playOsc(f, "sawtooth", now + i * 0.12, 0.5, 0.5, dest);
          playOsc(f * 2, "sine", now + i * 0.12, 0.4, 0.2, dest);
        });
        break;
      }

      case "truth": {
        // Mysterious descending tone
        playOsc(440, "sine", now, 0.3, 0.4, dest);
        playOsc(369.99, "sine", now + 0.2, 0.3, 0.3, dest);
        break;
      }

      case "dare": {
        // Dramatic low hit
        playOsc(150, "sawtooth", now, 0.2, 0.7, dest);
        playNoise(now, 0.15, 0.3, dest, 600);
        break;
      }

      case "crocodile_word": {
        // Playful boing
        const boing = ctx.createOscillator();
        const boingG = ctx.createGain();
        boing.type = "sine";
        boing.frequency.setValueAtTime(300, now);
        boing.frequency.exponentialRampToValueAtTime(600, now + 0.15);
        boing.frequency.exponentialRampToValueAtTime(400, now + 0.3);
        boingG.gain.setValueAtTime(0.6, now);
        boingG.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
        boing.connect(boingG);
        boingG.connect(dest);
        boing.start(now);
        boing.stop(now + 0.45);
        break;
      }

      default:
        break;
      }
    } catch (e) { /* ignore audio errors */ }
  }, [getCtx, playOsc, playNoise]);

  // ── Toggle controls ────────────────────────────────────────────────────────

  const toggleSound = useCallback(() => {
    const next = !soundEnabledRef.current;
    soundEnabledRef.current = next;
    setSoundEnabled(next);
    if (sfxGainRef.current) {
      sfxGainRef.current.gain.value = next ? 0.6 : 0;
    }
  }, []);

  const toggleMusic = useCallback(() => {
    const next = !musicEnabledRef.current;
    musicEnabledRef.current = next;
    setMusicEnabled(next);
    if (next) {
      startMusic();
    } else {
      stopMusic();
      if (musicGainRef.current) musicGainRef.current.gain.value = 0;
    }
  }, [startMusic, stopMusic]);

  // Start music automatically on mount (after first user gesture, handled by getCtx)
  const initAudio = useCallback(() => {
    getCtx();
    if (musicEnabledRef.current) startMusic();
  }, [getCtx, startMusic]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMusic();
      ctxRef.current?.close();
    };
  }, [stopMusic]);

  return {
    soundEnabled,
    musicEnabled,
    playSound,
    toggleSound,
    toggleMusic,
    initAudio,
    stopMusic,
  };
}